import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, Image } from 'react-native';
import { View, Text } from '@constants/Themed';
import { XStack, YStack, Button as TamaguiButton, Text as TamaguiText, Separator } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import { MaterialIcons, AntDesign } from '@expo/vector-icons';
import { StageSchema } from '../ActiveCampaigns';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import UserListItem from '../../UsersScreen/Components/UserListItem';
import ActionIcon from '@components/Global/ActionIcon/ActionIcon';

interface ModernStageCardProps {
  stage: StageSchema;
  index: number;
  totalStages: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (values: StageSchema) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  colors: { primary: string; secondary: string };
  theme: boolean;
}

const ModernStageCard: React.FC<ModernStageCardProps> = ({
  stage,
  index,
  totalStages,
  isExpanded,
  onToggleExpand,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  colors,
  theme
}) => {
  const stageSchema = {
    title: {
      type: 'string',
      validation: { required: true },
      helpText: "The name of the stage"
    },
    description: {
      type: 'string',
      validation: { required: true },
      helpText: "A brief description of this stage"
    },
    category: {
      type: 'string',
      validation: { required: true },
      helpText: "The category of the stage"
    },
    headline: {
      type: 'string',
      helpText: "The headline for this stage (used in subject lines, etc)"
    },
    owner: {
      type: 'string',
      helpText: "The team member responsible for this stage",
      ref: "user_stores"
    },
    stage_action: {
      type: 'string',
      helpText: "Select a template to automate actions at this stage",
      ref: "template_select"
    },
    when: {
      type: 'string',
      ref: "date_time",
      validation: { required: false },
      helpText: "When to execute this stage"
    },
  };

  return (
    <Animatable.View
      animation="fadeInUp"
      delay={index * 100}
      style={[
        styles.container,
        {
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderLeftWidth: 4,
          borderLeftColor: colors.primary,
        }
      ]}
    >
      {/* Stage Header - Always Visible */}
      <Pressable onPress={onToggleExpand}>
        <XStack
          style={{
            padding: 20,
            alignItems: 'center',
            gap: 16
          }}
        >
          {/* Stage Number Badge */}
          <View style={[
            styles.stageBadge,
            { backgroundColor: colors.secondary }
          ]}>
            <Text style={{
              fontSize: 24,
              fontWeight: '800',
              color: colors.primary
            }}>
              {index + 1}
            </Text>
          </View>

          {/* Stage Info */}
          <YStack flex={1} gap={4}>
            <XStack alignItems="center" gap={8}>
              <Text style={{
                fontSize: 18,
                fontWeight: '700',
                color: theme ? "#1e293b" : "#f1f5f9"
              }}>
                {stage.title}
              </Text>
              {stage.category && (
                <View style={{
                  backgroundColor: colors.secondary,
                  paddingHorizontal: 10,
                  paddingVertical: 4,
                  borderRadius: 12
                }}>
                  <Text style={{
                    fontSize: 11,
                    fontWeight: '600',
                    color: colors.primary
                  }}>
                    {stage.category.toUpperCase()}
                  </Text>
                </View>
              )}
            </XStack>

            <Text style={{
              fontSize: 14,
              color: theme ? "#64748b" : "#94a3b8",
              lineHeight: 20
            }}>
              {stage.description}
            </Text>

            {/* Stage Metadata */}
            <XStack gap={12} marginTop={8} flexWrap="wrap">
              {stage.owner && (
                <XStack
                  alignItems="center"
                  gap={6}
                  style={{
                    backgroundColor: theme ? "#f8fafc" : "#0f172a",
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                    borderRadius: 8
                  }}
                >
                  <MaterialIcons
                    name="person"
                    size={14}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                  <Text style={{
                    fontSize: 12,
                    color: theme ? "#64748b" : "#94a3b8",
                    fontWeight: '500'
                  }}>
                    {stage.owner.name} {stage.owner.surname}
                  </Text>
                </XStack>
              )}

              {stage.headline && (
                <XStack
                  alignItems="center"
                  gap={6}
                  style={{
                    backgroundColor: theme ? "#f8fafc" : "#0f172a",
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                    borderRadius: 8
                  }}
                >
                  <MaterialIcons
                    name="title"
                    size={14}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                  <Text style={{
                    fontSize: 12,
                    color: theme ? "#64748b" : "#94a3b8",
                    fontWeight: '500'
                  }}>
                    {stage.headline}
                  </Text>
                </XStack>
              )}

              {stage.stage_action && (
                <XStack
                  alignItems="center"
                  gap={6}
                  style={{
                    backgroundColor: theme ? "#dcfce7" : "#14532d",
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                    borderRadius: 8
                  }}
                >
                  <MaterialIcons
                    name="flash-on"
                    size={14}
                    color={theme ? "#059669" : "#10b981"}
                  />
                  <Text style={{
                    fontSize: 12,
                    color: theme ? "#059669" : "#10b981",
                    fontWeight: '600'
                  }}>
                    Automated
                  </Text>
                </XStack>
              )}
            </XStack>
          </YStack>

          {/* Expand Icon */}
          <MaterialIcons
            name={isExpanded ? "expand-less" : "expand-more"}
            size={28}
            color={theme ? "#94a3b8" : "#64748b"}
          />
        </XStack>
      </Pressable>

      {/* Stage Actions - Always Visible */}
      <XStack
        style={{
          paddingHorizontal: 20,
          paddingBottom: 16,
          gap: 8,
          flexWrap: 'wrap'
        }}
      >
        <TamaguiButton
          size="$2"
          chromeless
          onPress={onToggleExpand}
          style={{
            borderWidth: 1.5,
            borderColor: colors.primary,
            backgroundColor: colors.secondary
          }}
          icon={<MaterialIcons name="edit" size={14} color={colors.primary} />}
        >
          <TamaguiText style={{
            color: colors.primary,
            fontSize: 13,
            fontWeight: '600'
          }}>
            Edit
          </TamaguiText>
        </TamaguiButton>

        {onMoveUp && (
          <TamaguiButton
            size="$2"
            chromeless
            onPress={onMoveUp}
            style={{
              borderWidth: 1.5,
              borderColor: theme ? "#94a3b8" : "#64748b",
              backgroundColor: theme ? "#f8fafc" : "#1e293b"
            }}
            icon={<MaterialIcons name="arrow-upward" size={14} color={theme ? "#64748b" : "#94a3b8"} />}
          />
        )}

        {onMoveDown && (
          <TamaguiButton
            size="$2"
            chromeless
            onPress={onMoveDown}
            style={{
              borderWidth: 1.5,
              borderColor: theme ? "#94a3b8" : "#64748b",
              backgroundColor: theme ? "#f8fafc" : "#1e293b"
            }}
            icon={<MaterialIcons name="arrow-downward" size={14} color={theme ? "#64748b" : "#94a3b8"} />}
          />
        )}

        <TamaguiButton
          size="$2"
          chromeless
          onPress={onDelete}
          style={{
            borderWidth: 1.5,
            borderColor: theme ? "#fecaca" : "#7f1d1d",
            backgroundColor: theme ? "#fef2f2" : "#450a0a",
            marginLeft: 'auto'
          }}
          icon={<AntDesign name="delete" size={14} color={theme ? "#ef4444" : "#f87171"} />}
        >
          <TamaguiText style={{
            color: theme ? "#ef4444" : "#f87171",
            fontSize: 13,
            fontWeight: '600'
          }}>
            Delete
          </TamaguiText>
        </TamaguiButton>
      </XStack>

      {/* Expanded Edit Form */}
      {isExpanded && (
        <Animatable.View
          animation="fadeInDown"
          duration={300}
          style={{
            borderTopWidth: 1,
            borderTopColor: theme ? "#e2e8f0" : "#334155",
            backgroundColor: theme ? "#f8fafc" : "#0f172a",
            maxHeight: 500
          }}
        >
          <View style={{
            padding: 20,
            backgroundColor: "transparent"
          }}>
            <XStack alignItems="center" gap={12} marginBottom={16}>
              <View style={{
                backgroundColor: colors.secondary,
                borderRadius: 10,
                padding: 8
              }}>
                <MaterialIcons
                  name="edit-note"
                  size={20}
                  color={colors.primary}
                />
              </View>
              <Text style={{
                fontSize: 16,
                fontWeight: '700',
                color: theme ? "#1e293b" : "#f1f5f9"
              }}>
                Edit Stage Details
              </Text>
            </XStack>

            <GenerateSchemaForm
              retainInitialValues
              schema={stageSchema}
              buttonLabel="Update Stage"
              submitFunction={(values) => {
                onUpdate(values);
              }}
              initialData={stage}
              scrollEnabled={false}
              noBackground
            />
          </View>
        </Animatable.View>
      )}

      {/* Template Preview (if stage_action exists) */}
      {stage.stage_action && !isExpanded && (
        <View style={{
          padding: 20,
          paddingTop: 0,
          backgroundColor: "transparent"
        }}>
          <View style={{
            backgroundColor: theme ? "#f8fafc" : "#0f172a",
            borderRadius: 12,
            padding: 16,
            borderWidth: 1,
            borderColor: theme ? "#e2e8f0" : "#334155"
          }}>
            <XStack alignItems="center" gap={12}>
              <View style={{
                backgroundColor: theme ? "#dcfce7" : "#14532d",
                borderRadius: 8,
                padding: 8
              }}>
                <MaterialIcons
                  name="auto-awesome"
                  size={20}
                  color={theme ? "#059669" : "#10b981"}
                />
              </View>
              <YStack flex={1}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? "#1e293b" : "#f1f5f9",
                  marginBottom: 4
                }}>
                  Automated Template
                </Text>
                <Text style={{
                  fontSize: 12,
                  color: theme ? "#64748b" : "#94a3b8"
                }}>
                  This stage uses an automated template
                </Text>
              </YStack>
              {stage.stage_action.snapShot && (
                <Image
                  source={{ uri: stage.stage_action.snapShot }}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    resizeMode: 'cover'
                  }}
                />
              )}
            </XStack>
          </View>
        </View>
      )}

      {/* Progress Connector (except for last stage) */}
      {index < totalStages - 1 && (
        <View style={{
          position: 'absolute',
          left: 41,
          bottom: -20,
          width: 4,
          height: 20,
          backgroundColor: colors.primary,
          opacity: 0.3,
          zIndex: -1
        }} />
      )}
    </Animatable.View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 6,
    overflow: 'visible',
    position: 'relative',
  },
  stageBadge: {
    width: 56,
    height: 56,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 3,
  }
});

export default ModernStageCard;

